'use client';

import { lazy, Suspense, useState } from 'react';
import {
  Check,
  FileArchive,
  FileText,
  Layers3,
  LoaderCircle,
  PackageCheck,
  ReceiptText,
  ShoppingBag,
} from 'lucide-react';
import { isVisibleArtworkObject } from '../domain/design';
import { createConfiguratorPriceQuote, formatMoney } from '../domain/pricing';
import type { ProductConfig, ProductViewId } from '../domain/types';
import { DEFAULT_A4_PAGE_PADDING_MM, inspectA4SheetFit } from '../export/a4-sheet';
import { deriveTransferGroups } from '../export/transfer-groups';
import { resolveProductColour, resolveProductVariant, resolveSizeProfile } from '../products';
import { useDesignSnapshot } from '../store/react';
import { GarmentMockup } from './GarmentMockup';
import { formatMm, SummaryRow } from './OptionsStep';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const SummaryDesignPreview = lazy(() =>
  import('./SummaryDesignPreview').then(module => ({
    default: module.SummaryDesignPreview,
  })),
);

export function SummaryStep({
  product,
  added,
  submissionReference,
  submissionError,
}: {
  product: ProductConfig;
  added: boolean;
  submissionReference?: string;
  submissionError?: string;
}) {
  const { t } = useTranslations();
  const { state } = useDesignSnapshot();
  const design = state.design;
  const variant = resolveProductVariant(product, design.variantId) ?? product.variants[0]!;
  const colour = resolveProductColour(product, design.colourId, variant.id);
  const profile = resolveSizeProfile(product, variant.id, design.size);
  const usedSurfaces = product.printableAreas.filter(area =>
    design.surfaces[area.viewId]?.objects.some(isVisibleArtworkObject),
  );
  const usedViewIds = usedSurfaces.map(area => area.viewId);
  const transferGroups = deriveTransferGroups(design, product);
  const [requestedPreviewViewId, setRequestedPreviewViewId] = useState<ProductViewId>(() =>
    resolveSummaryPreviewView(state.activeViewId, usedViewIds),
  );
  const [activeDownload, setActiveDownload] = useState<'drawing' | 'quote' | 'production'>();
  const [downloadError, setDownloadError] = useState<string>();
  const [downloadSuccess, setDownloadSuccess] = useState<string>();
  const previewViewId = usedViewIds.includes(requestedPreviewViewId)
    ? requestedPreviewViewId
    : resolveSummaryPreviewView(state.activeViewId, usedViewIds);
  const priceQuote = createConfiguratorPriceQuote(product, design);
  const a4PagePaddingMm =
    product.productionRules.workflow?.pagePaddingMm ?? DEFAULT_A4_PAGE_PADDING_MM;

  const downloadOutput = async (kind: 'drawing' | 'quote' | 'production') => {
    setActiveDownload(kind);
    setDownloadError(undefined);
    setDownloadSuccess(undefined);
    try {
      const exporters = await import('../export');
      let blob: Blob;
      let filename: string;
      if (kind === 'drawing') {
        const output = await exporters.createManufacturingDrawingPdf({ design, product });
        blob = output.blob;
        filename = output.filename;
      } else if (kind === 'quote') {
        const output = await exporters.createPriceQuotePdf({ design, product });
        blob = output.blob;
        filename = output.filename;
      } else {
        const output = await exporters.exportProductionBundle({ design, product });
        blob = output.zip;
        filename = output.zipFilename;
      }
      if (!blob.size) throw new Error('Vygenerovaný súbor je prázdny.');
      downloadBlob(blob, filename);
      setDownloadSuccess(filename);
    } catch (error) {
      setDownloadError(
        error instanceof Error ? error.message : 'Súbor sa nepodarilo pripraviť. Skúste to znova.',
      );
    } finally {
      setActiveDownload(undefined);
    }
  };

  return (
    <section className="step-content summary-step">
      <div className="content-heading">
        <div>
          <p className="eyebrow">5 / 5 · Zhrnutie</p>
          <h2>Váš návrh je pripravený</h2>
        </div>
        <p>
          Skontrolujte produkt, množstvo, motívy a priebežnú cenovú rekapituláciu pred potvrdením
          konfigurácie.
        </p>
      </div>
      {added && (
        <div className="cart-success">
          <span>
            <Check />
          </span>
          <div>
            <strong>Konfigurácia je pripravená</strong>
            <p>
              Návrh, farba, veľkosť a počet kusov sú uložené.
              {submissionReference ? ` Referencia: ${submissionReference}` : ''}
            </p>
          </div>
        </div>
      )}
      {submissionError && <p className="export-error">{submissionError}</p>}
      <div className="summary-layout">
        <div className="summary-primary">
          <div className="summary-visual">
            <GarmentMockup
              product={product}
              colour={colour.hex}
              variantId={variant.id}
              size={design.size}
              viewId={previewViewId}
            >
              {usedViewIds.includes(previewViewId) && (
                <Suspense fallback={null}>
                  <SummaryDesignPreview product={product} design={design} viewId={previewViewId} />
                </Suspense>
              )}
            </GarmentMockup>
            <div className="surface-chip-row">
              {usedSurfaces.map(area => (
                <button
                  type="button"
                  key={area.viewId}
                  className={area.viewId === previewViewId ? 'active' : ''}
                  aria-pressed={area.viewId === previewViewId}
                  onClick={() => setRequestedPreviewViewId(area.viewId)}
                >
                  {area.label} ·{' '}
                  {design.surfaces[area.viewId]?.objects.filter(isVisibleArtworkObject).length}{' '}
                  prvkov
                </button>
              ))}
            </div>
          </div>
          <div className="summary-card summary-output-card">
            <div className="card-heading">
              <h3>Výstupy na stiahnutie</h3>
              <FileArchive />
            </div>
            <div className="summary-output-list">
              <article className="summary-output-item">
                <span className="summary-output-icon">
                  <FileText />
                </span>
                <div>
                  <strong>Výrobný výkres</strong>
                  <p>Tričko s návrhom, číslami transferov a ich polohou voči osi trička.</p>
                  <small>vyrobny-vykres.pdf</small>
                </div>
                <button
                  type="button"
                  disabled={Boolean(activeDownload)}
                  onClick={() => void downloadOutput('drawing')}
                >
                  {activeDownload === 'drawing' && <LoaderCircle className="spin-icon" />}
                  {t('text717')} PDF
                </button>
              </article>
              <article className="summary-output-item">
                <span className="summary-output-icon">
                  <ReceiptText />
                </span>
                <div>
                  <strong>Cenová ponuka</strong>
                  <p>Fotografia trička, konfigurácia a jednoduchý rozpis položiek.</p>
                  <small>cenova-ponuka.pdf</small>
                </div>
                <button
                  type="button"
                  disabled={Boolean(activeDownload)}
                  onClick={() => void downloadOutput('quote')}
                >
                  {activeDownload === 'quote' && <LoaderCircle className="spin-icon" />}
                  {t('text717')} PDF
                </button>
              </article>
              <article className="summary-output-item production">
                <span className="summary-output-icon">
                  <FileArchive />
                </span>
                <div>
                  <strong>Výrobné podklady</strong>
                  <p>Iba finálne páry preview PNG + A4 Print&amp;Cut PDF pre každý transfer.</p>
                  <small>production.zip</small>
                </div>
                <button
                  type="button"
                  disabled={Boolean(activeDownload)}
                  onClick={() => void downloadOutput('production')}
                >
                  {activeDownload === 'production' && <LoaderCircle className="spin-icon" />}
                  Stiahnuť ZIP
                </button>
              </article>
            </div>
            {downloadSuccess && (
              <p className="export-success">
                <Check /> Hotovo: {downloadSuccess}
              </p>
            )}
            {downloadError && <p className="export-error">{downloadError}</p>}
          </div>
        </div>
        <div className="summary-details">
          <div className="summary-card">
            <div className="card-heading">
              <h3>Konfigurácia</h3>
              <PackageCheck />
            </div>
            <SummaryRow label="Produkt" value={product.name} />
            <SummaryRow label="Variant" value={variant.label} />
            <SummaryRow label="Veľkosť" value={design.size} />
            <SummaryRow label="Počet" value={`${design.quantity} ks`} />
            <SummaryRow
              label="Rozmer naplocho"
              value={`${formatMm(profile.garmentMm.flatWidth)} × ${formatMm(profile.garmentMm.bodyLength)} mm`}
            />
            <SummaryRow label="Farba" value={colour.name} dot={colour.hex} />
            <SummaryRow label="Výrobné motívy" value={String(transferGroups.length)} />
          </div>
          <div className="summary-card">
            <div className="card-heading">
              <h3>Potlač</h3>
              <Layers3 />
            </div>
            {transferGroups.length ? (
              transferGroups.map(transfer => {
                const a4Fit = inspectA4SheetFit(transfer.artworkBoundsMm, a4PagePaddingMm);
                return (
                  <div className="print-summary" key={transfer.id}>
                    <span>{transfer.label}</span>
                    <strong>
                      {formatMm(transfer.artworkBoundsMm.width)} ×{' '}
                      {formatMm(transfer.artworkBoundsMm.height)} mm
                    </strong>
                    <small>
                      Samostatný A4 podklad · {transfer.objectIds.length}{' '}
                      {transfer.objectIds.length === 1 ? 'prvok' : 'prvky'} ·{' '}
                      {formatCopyCount(design.quantity)}
                    </small>
                    <small className={a4Fit.fits ? 'a4-fit-ok' : 'a4-fit-error'}>
                      {a4Fit.fits
                        ? `A4 ${formatA4Orientation(a4Fit.layout.orientation)} · mierka 1 : 1 ✓`
                        : 'Motív sa v mierke 1 : 1 nezmestí na jeden hárok A4'}
                    </small>
                  </div>
                );
              })
            ) : (
              <p className="muted">Bez pridaného dizajnu.</p>
            )}
            <p className="production-calibration-note">
              <strong>Rozloženie zostane zachované.</strong> Blízke prvky ostanú spolu. Ak sa na
              jeden hárok A4 nezmestia, pripravia sa ako samostatné výrobné motívy s presnou polohou
              na tričku.
            </p>
          </div>
          <div className="quote-card" aria-label="Priebežná cenová ponuka">
            <div className="quote-heading">
              <span>
                <ReceiptText />
                <span>
                  <small>Cenová rekapitulácia</small>
                  <strong>Priebežná ponuka</strong>
                </span>
              </span>
              <b>Orientačná</b>
            </div>
            <dl className="quote-lines">
              <div>
                <dt>Tričko / ks</dt>
                <dd>{formatMoney(priceQuote.baseUnitCents, priceQuote.currency)}</dd>
              </div>
              {priceQuote.variantUnitDeltaCents !== 0 && (
                <div>
                  <dt>Príplatok za variant / ks</dt>
                  <dd>{formatMoney(priceQuote.variantUnitDeltaCents, priceQuote.currency)}</dd>
                </div>
              )}
              <div>
                <dt>
                  Potlač ({formatPrintSideCount(priceQuote.printedSurfaceCount)} ·{' '}
                  {priceQuote.transferSheetCount}× A4 / ks)
                </dt>
                <dd>{formatMoney(priceQuote.printUnitCents, priceQuote.currency)}</dd>
              </div>
              <div>
                <dt>Množstvo</dt>
                <dd>{priceQuote.quantity} ks</dd>
              </div>
            </dl>
            <div className="quote-total">
              <span>
                <small>Spolu</small>
                <strong>
                  {formatMoney(priceQuote.configuredSubtotalCents, priceQuote.currency)}
                </strong>
              </span>
              <small>{formatMoney(priceQuote.configuredUnitCents, priceQuote.currency)} / ks</small>
            </div>
            <p className="quote-disclaimer">
              Cena zahŕňa tričká a vypočítané A4 transfery. DPH a doprava sa doplnia podľa spôsobu
              objednávky.
            </p>
          </div>
          <div className="summary-assurance">
            <ShoppingBag />
            <span>
              <strong>Kompletná konfigurácia objednávky</strong> Pri odovzdaní zostane zachovaná
              veľkosť, farba, množstvo aj presné rozloženie potlače oboch strán.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function resolveSummaryPreviewView(
  activeViewId: ProductViewId,
  usedViewIds: readonly ProductViewId[],
): ProductViewId {
  if (usedViewIds.includes(activeViewId)) return activeViewId;
  if (usedViewIds.includes('front')) return 'front';
  return usedViewIds[0] ?? 'front';
}

function formatCopyCount(quantity: number): string {
  if (quantity === 1) return '1 kópia';
  if (quantity >= 2 && quantity <= 4) return `${quantity} kópie`;
  return `${quantity} kópií`;
}

function formatPrintSideCount(count: number): string {
  if (count === 1) return '1 strana';
  if (count >= 2 && count <= 4) return `${count} strany`;
  return `${count} strán`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatA4Orientation(orientation: 'portrait' | 'landscape'): string {
  return orientation === 'portrait' ? 'na výšku' : 'na šírku';
}
